import { Controller } from "@hotwired/stimulus"

// Tag-style multi-select built on a single <select> dropdown + hidden inputs
// Usage in view:
// <div data-controller="tag-select"
//      data-tag-select-hidden-name-value="profile[services][]">
//   <div data-tag-select-target="tags"></div>
//   <select data-tag-select-target="select" data-action="change->tag-select#add">
//     <option value="">Add a service…</option>
//     ...
//   </select>
// </div>
export default class extends Controller {
  static targets = ["select", "tags", "error", "input"]
  static values = {
    hiddenName: String,
    formId: String,
    required: Boolean,
    requiredMessage: String
  }

  connect() {
    this.selected = new Set()
    this._submissionAttempted = false
    this._attachFormListener()
    // Initialize from any existing hidden inputs (server-rendered)
    this._scanExisting()
    this._refreshDisabledOptions()
    this._updateValidityState()
  }

  disconnect() {
    if (this.formElement && this._submitHandler) {
      this.formElement.removeEventListener("submit", this._submitHandler)
    }
  }

  add(event) {
    if (!this.hasSelectTarget) return
    const opt = this.selectTarget.options[this.selectTarget.selectedIndex]
    const value = opt?.value
    const label = opt?.text
    if (!value) return
    if (this.selected.has(value)) {
      this.selectTarget.selectedIndex = 0
      return
    }
    this._appendTag(value, label)
    this.selected.add(value)
    this._refreshDisabledOptions()
    this._updateValidityState()
    this._dispatchChange()
    // Reset selection to placeholder
    this.selectTarget.selectedIndex = 0
  }

  remove(event) {
    const value = event.currentTarget?.dataset?.value
    if (!value) return
    // Remove tag element
    const tagEl = this.tagsTarget.querySelector(`[data-tag-select-tag][data-value="${CSS.escape(value)}"]`)
    if (tagEl) tagEl.remove()
    // Remove hidden input
    const hidden = this.tagsTarget.querySelector(`input[type="hidden"][name="${this.hiddenNameValue}"][value="${CSS.escape(value)}"]`)
    if (hidden) hidden.remove()
    // Update set and re-enable option
    this.selected.delete(value)
    this._refreshDisabledOptions()
    this._updateValidityState()
    this._dispatchChange()
  }

  commitInput(event) {
    if (!this.hasInputTarget) return
    if (event) event.preventDefault()
    const value = this._sanitizeCustomValue(this.inputTarget.value)
    this._addCustomValue(value)
  }

  _scanExisting() {
    const existing = this.tagsTarget.querySelectorAll(`input[type="hidden"][name="${this.hiddenNameValue}"]`)
    existing.forEach((input) => this.selected.add(input.value))
  }

  _refreshDisabledOptions() {
    if (!this.hasSelectTarget) return
    Array.from(this.selectTarget.options).forEach((o, idx) => {
      if (idx === 0) return // placeholder
      o.disabled = this.selected.has(o.value)
    })
  }

  _attachFormListener() {
    if (!this.requiredValue) return
    this.formElement = this._resolveForm()
    if (!this.formElement) return
    this._submitHandler = this._submitHandler || ((event) => this._handleSubmit(event))
    this.formElement.addEventListener("submit", this._submitHandler)
  }

  _resolveForm() {
    if (this.hasFormIdValue && this.formIdValue) {
      return document.getElementById(this.formIdValue)
    }
    return this.element.closest("form")
  }

  _handleSubmit(event) {
    if (!this.requiredValue) return
    this._submissionAttempted = true
    if (this._hasSelection()) {
      this._clearError()
      return
    }
    event.preventDefault()
    event.stopImmediatePropagation()
    this._showRequiredMessage()
    this.selectTarget.reportValidity()
    this.selectTarget.focus()
  }

  _hasSelection() {
    return this.selected.size > 0
  }

  _requiredMessage() {
    if (this.hasRequiredMessageValue && this.requiredMessageValue?.trim()) {
      return this.requiredMessageValue
    }
    return "Please select at least one option."
  }

  _showRequiredMessage() {
    if (!this.requiredValue) return
    if (!this.hasSelectTarget) return
    const message = this._requiredMessage()
    this.selectTarget.setCustomValidity(message)
    this.selectTarget.setAttribute("aria-invalid", "true")
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
    }
  }

  _clearError() {
    if (!this.requiredValue) return
    if (!this.hasSelectTarget) return
    this.selectTarget.setCustomValidity("")
    this.selectTarget.removeAttribute("aria-invalid")
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = ""
      this.errorTarget.classList.add("hidden")
    }
  }

  _updateValidityState() {
    if (!this.requiredValue) return
    if (this._hasSelection()) {
      this._clearError()
    } else if (this._submissionAttempted) {
      this._showRequiredMessage()
    }
  }

  _appendTag(value, label) {
    const wrapper = document.createElement("div")
    wrapper.dataset.tagSelectTag = ""
    wrapper.dataset.value = value
    wrapper.className = "inline-flex items-center gap-1 bg-[#084572]/10 text-[#084572] px-2 py-1 rounded-full text-xs mr-2 mb-2"

    const text = document.createElement("span")
    text.textContent = label || value

    const btn = document.createElement("button")
    btn.type = "button"
    btn.dataset.action = "tag-select#remove"
    btn.dataset.value = value
    btn.className = "hover:text-red-600 focus:outline-none"
    btn.setAttribute("aria-label", `Remove ${label || value}`)
    btn.textContent = "×"

    const hidden = document.createElement("input")
    hidden.type = "hidden"
    hidden.name = this.hiddenNameValue
    hidden.value = value

    wrapper.appendChild(text)
    wrapper.appendChild(btn)
    if (this.hasFormIdValue && this.formIdValue) {
      hidden.setAttribute("form", this.formIdValue)
    }
    wrapper.appendChild(hidden)
    this.tagsTarget.appendChild(wrapper)
  }

  _addCustomValue(value) {
    if (!value) {
      this._resetInput()
      return
    }
    if (this.selected.has(value)) {
      this._resetInput()
      return
    }
    this._appendTag(value, value)
    this.selected.add(value)
    this._refreshDisabledOptions()
    this._updateValidityState()
    this._dispatchChange()
    this._resetInput()
  }

  _sanitizeCustomValue(value) {
    if (typeof value !== "string") return ""
    return value.trim().replace(/,+$/, "")
  }

  _resetInput() {
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
    }
  }

  _dispatchChange() {
    // Dispatch change event on the form to notify unsaved-changes controller
    const form = this._resolveForm()
    if (form) {
      form.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }
}
